import green from '@material-ui/core/colors/green';
import red from '@material-ui/core/colors/red';
import yellow from '@material-ui/core/colors/yellow';
import { formatRelative } from 'date-fns';
import localePtBR from 'date-fns/locale/pt-BR';
import * as Rx from 'rxjs';
import { NOTIFICATION_SOCKET_URL } from 'settings';
import io from 'socket.io-client';
import { ENotificationType } from 'v2/interfaces/notification';
import webNotificationService from 'v2/services/webNotification';

import type { INotification, IGroupedNotification } from 'v2/interfaces/notification';
import type { WebNotificationService } from 'v2/services/webNotification';

import eduzzAccountsService from './eduzzAccount';
import tokenService from './token';

import type { EduzzAccountsService } from './eduzzAccount';
import type { TokenService } from './token';

export class NotificationService {
  private socket: SocketIOClient.Socket = {} as SocketIOClient.Socket;
  private listGroupNotifications$ = new Rx.BehaviorSubject<IGroupedNotification[]>([]);
  private lastUpdate$ = new Rx.BehaviorSubject<string>(null);
  private loadingNotifications$ = new Rx.BehaviorSubject<boolean>(false);
  private serverIsListening$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(
    private tokenService: TokenService,
    private webNotification: WebNotificationService,
    private eduzzAccountsService: EduzzAccountsService
  ) {
    this.eduzzAccountsService.getUser().subscribe((user) => {
      if (!user) return this.disconnect();

      const isPartner = user?.acceptence_terms;
      const userId = user?.id;
      if (isPartner && userId) {
        this.init(userId);
      }
    });
  }

  isLoadingNotifications = () => this.loadingNotifications$;

  public disconnect(): void {
    if (this.socket?.connected && this.socket?.disconnect) this.socket.disconnect();
  }

  public getListGroupNotifications = (): Rx.Observable<IGroupedNotification[]> => this.listGroupNotifications$;

  public getLastUpdate = (): Rx.Observable<string> => this.lastUpdate$;

  public getListNotifications = (type?: ENotificationType) => {
    this.loadingNotifications$.next(true);

    const observer = new Rx.Observable<INotification[]>((observer) => {
      this.socket.on('list_notifications', (data: INotification[]) => observer.next(data));
    });

    observer.subscribe((notifications) => {
      this.loadingNotifications$.next(false);

      return notifications;
    });

    this.serverIsListening$.asObservable().subscribe((isConnected) => isConnected && this.listNotification(type));

    return observer;
  };

  public listNotification(type?: ENotificationType) {
    this.socket.emit('list_notification', { size: 200, type });
  }

  public readNotificationByType(type: ENotificationType) {
    this.socket.emit('read_notification_by_type', type);
  }

  public readAllNotifications() {
    this.socket.emit('read_all_notifications');
  }

  public formatNotification(notification: INotification<{ fatCod?: string; url_download?: string }>) {
    type FormatType = { [key in ENotificationType]: { color: string; icon: string; redirect?: string; redirectDesktop?: string } };

    const invoiceId = notification.payload.fatCod;

    const types: FormatType = {
      [ENotificationType.SALES]: {
        color: green[500],
        icon: 'attach_money',
        redirect: `/producer/invoice/details/${invoiceId}`,
        redirectDesktop: `/producer/invoice/details/${invoiceId}`,
      },
      [ENotificationType.COMISSION]: {
        color: green[500],
        icon: 'attach_money',
        redirect: `/producer/invoice/details/${invoiceId}`,
        redirectDesktop: `/producer/invoice/details/${invoiceId}`,
      },
      [ENotificationType.APPROVED_PRODUCT]: {
        color: green[500],
        icon: 'check_outlined',
        redirect: '/producer/product/myproducts/approved',
        redirectDesktop: '/producer/product/myproducts/approved',
      },
      [ENotificationType.DISAPPROVED_PRODUCT]: {
        color: yellow[500],
        icon: 'priority_high_outlined',
        redirect: '/producer/product/myproducts/reproved',
        redirectDesktop: '/producer/product/myproducts/reproved',
      },
      [ENotificationType.REFUND]: {
        color: green[500],
        icon: 'refresh_outlined',
        redirect: '/producer/request-management',
        redirectDesktop: '/producer/request-management',
      },
      [ENotificationType.REPORT]: {
        color: green[500],
        icon: 'description_oulined',
        redirect: notification.payload.url_download,
        redirectDesktop: notification.payload.url_download,
      },
      [ENotificationType.REPORT_NO_DATA]: {
        color: yellow[500],
        icon: 'description_oulined',
        redirectDesktop: '/producer/notifications/report_ready_without_data',
      },
      [ENotificationType.BANKSLIP_GENERATED]: {
        color: green[500],
        icon: 'attach_money',
        redirect: `/producer/invoice/details/${invoiceId}`,
        redirectDesktop: `/producer/invoice/details/${invoiceId}`,
      },
      [ENotificationType.NEW_CHARGEBACK]: {
        color: yellow[500],
        icon: 'refresh_outlined',
        redirect: '/producer/chargebacks',
        redirectDesktop: '/producer/chargebacks',
      },
      [ENotificationType.HIGH_CHARGEBACK_RATE]: {
        color: red[500],
        icon: 'refresh_outlined',
        redirect: '/producer/chargebacks',
        redirectDesktop: '/producer/chargebacks',
      },
    };

    return {
      ...notification,
      ...types[notification.type],
    };
  }

  private setUpdateDate() {
    this.lastUpdate$.next(formatRelative(new Date(), new Date(), { locale: localePtBR }));
  }

  private sendNotification(newNotification: INotification) {
    const formattedNotification = this.formatNotification(newNotification);

    return this.webNotification.newNotification(formattedNotification.title, formattedNotification.message, undefined, () => {
      if (formattedNotification.redirectDesktop) window.open(formattedNotification.redirectDesktop);
    });
  }

  private init(userId: number) {
    this.tokenService.getAccountsSsid().subscribe((token) => {
      if (!token || token.length === 0) {
        return;
      }

      this.disconnect();

      this.socket = io(NOTIFICATION_SOCKET_URL, {
        query: { token, userId },
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
      });

      this.socket.on('connect', () => {
        console.info('Connected');
      });

      this.socket.on('connect_error', (error: any) => {
        this.serverIsListening$.next(false);
        console.warn('error', error);
      });

      this.socket.on('disconnect', () => {
        this.serverIsListening$.next(false);
        console.info('Disconnected');
      });

      new Rx.Observable<number>((observer) => {
        this.socket.on('user_id', (userId: number) => observer.next(userId));
      }).subscribe((userId) => userId && this.serverIsListening$.next(true));

      new Rx.Observable<INotification>((observer) => {
        this.socket.on('new_notification', (data: INotification) => observer.next(data));
      }).subscribe((notification) => {
        let groupedList = this.listGroupNotifications$.value;

        const alreadyListed = groupedList.filter((group) => group.type === notification.type).length > 0;

        if (!alreadyListed) {
          groupedList.push({
            userId: notification.userId,
            type: notification.type,
            needAction: notification.needAction,
            quantity: 0,
          });
        }

        groupedList = groupedList.map((group) => {
          if (group.type === notification.type) {
            group.quantity += 1;
          }
          return group;
        });

        this.listGroupNotifications$.next(groupedList);
        this.setUpdateDate();
        if (!notification.readed) this.sendNotification(notification);
      });

      new Rx.Observable<INotification>((observer) => {
        this.socket.on('readed_notification', (notification: INotification) => observer.next(notification));
      }).subscribe((notification) => {
        let groupedList = this.listGroupNotifications$.value;

        groupedList = groupedList.map((group) => {
          if (group.type === notification.type) {
            if (group.quantity > 0) {
              group.quantity -= 1;
            }

            if (notification.type === ENotificationType.REFUND && group.dueDates) {
              group.dueDates = group.dueDates.filter((dueDate) => dueDate.id !== notification.id);
            }
          }

          return group;
        });

        this.listGroupNotifications$.next(groupedList);
        this.setUpdateDate();
      });

      new Rx.Observable<string>((observer) => {
        this.socket.on('readed_notification_by_type', (type: string) => observer.next(type));
      }).subscribe((type) => {
        let groupedList = this.listGroupNotifications$.value;

        groupedList = groupedList.map((group) => {
          if (group.type === type) {
            group.quantity = 0;
          }
          return group;
        });

        this.listGroupNotifications$.next(groupedList);
        this.setUpdateDate();
      });

      new Rx.Observable<IGroupedNotification[]>((observer) => {
        this.socket.on('list_grouped_notifications', (data: IGroupedNotification[]) => observer.next(data));
      }).subscribe((notifications) => {
        this.listGroupNotifications$.next(notifications);
        this.setUpdateDate();
      });
    });
  }
}

const notificationService = new NotificationService(tokenService, webNotificationService, eduzzAccountsService);
export default notificationService;
